import React from 'react';
import { useTranslation } from 'react-i18next';

interface Review {
  id: string;
  name: string;
  date: string;
  rating: number;
  title: string;
  content: string;
  version: string;
  country: string;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const avatarGradients = [
  'linear-gradient(135deg, #4F46E5, #a78bfa)',
  'linear-gradient(135deg, #0ea5e9, #38bdf8)',
  'linear-gradient(135deg, #f59e0b, #fbbf24)',
  'linear-gradient(135deg, #10b981, #34d399)',
  'linear-gradient(135deg, #ef4444, #f87171)',
];

const getGradient = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return avatarGradients[hash % avatarGradients.length];
};

const ReviewCard = ({
  review,
  verifiedPurchaseText,
}: {
  review: Review;
  verifiedPurchaseText: string;
}) => (
  <div
    style={{
      background: 'var(--v2-reviews-card)',
      border: '1px solid var(--v2-reviews-border)',
      borderRadius: '18px',
      padding: '28px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
    onMouseEnter={(e) => {
      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 16px 48px rgba(0,0,0,0.3)';
    }}
    onMouseLeave={(e) => {
      (e.currentTarget as HTMLDivElement).style.transform = 'none';
      (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
    }}
  >
    {/* Stars */}
    <div style={{ color: '#fbbf24', fontSize: '16px', letterSpacing: '1px' }}>★★★★★</div>

    {/* Title & content */}
    <div>
      <h3
        style={{
          fontSize: '15px',
          fontWeight: 700,
          color: '#fff',
          marginBottom: '8px',
        }}
      >
        {review.title}
      </h3>
      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, fontStyle: 'italic' }}>
        "{review.content}"
      </p>
    </div>

    {/* Footer row */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Avatar */}
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: getGradient(review.name),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          {getInitials(review.name)}
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
            {review.name}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{review.date}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{verifiedPurchaseText}</span>
      </div>
    </div>
  </div>
);

const Reviews = () => {
  const { t } = useTranslation();

  const reviewsData = Array.isArray(t('reviews.reviewsData', { returnObjects: true }))
    ? (t('reviews.reviewsData', { returnObjects: true }) as any[]).map((review, index) => ({
        ...review,
        id: review.id || (index + 1).toString(),
        rating: review.rating || 5,
      }))
    : [
        {
          id: '1',
          name: 'Cornelia Camelia',
          date: 'Jul 27, 2025',
          rating: 5,
          title: 'Really great transcription app!!',
          content:
            'Notely really helps me become more efficient at work. Writes really good notes and can easily summarize and chat.',
          version: 'Version 1.3.3',
          country: 'Sweden',
        },
        {
          id: '2',
          name: 'Jacob Joon',
          date: 'Jun 21, 2025',
          rating: 5,
          title: 'This is such an amazing app',
          content:
            'I have been using this for client meetings. The transcription is spot-on and the smart summarisation saves me time. Worth trying out!',
          version: 'Version 1.2.7',
          country: 'United States',
        },
        {
          id: '3',
          name: 'Helga F. S.',
          date: 'Mar 5, 2025',
          rating: 5,
          title: 'So efficient!',
          content:
            'Finally I can be more present in my meetings while Notely AI is taking the notes and summarizes actions/key points for me. I can highly recommend!',
          version: 'Version 1.1.0',
          country: 'Sweden',
        },
      ];

  return (
    <section style={{ background: 'var(--v2-reviews-bg)', position: 'relative', zIndex: 2 }} className="py-24 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          {/* Rating pill */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(251,191,36,0.12)',
              border: '1px solid rgba(251,191,36,0.25)',
              color: '#fbbf24',
              padding: '6px 16px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '20px',
            }}
          >
            {t('reviews.averageRating', { rating: 4.9 })}
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(91,74,231,0.18)',
              color: 'var(--v2-primary)',
              padding: '5px 14px',
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '20px',
              marginLeft: '10px',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--v2-primary)',
                animation: 'blink 1.5s ease-in-out infinite',
              }}
            />
            {t('reviews.badge')}
          </div>

          <h2
            style={{
              fontSize: 'clamp(28px, 3.5vw, 40px)',
              fontWeight: 800,
              color: '#fff',
              marginBottom: '12px',
              display: 'block',
            }}
          >
            {t('reviews.title')}
          </h2>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.5)', maxWidth: '500px', margin: '0 auto' }}>
            {t('reviews.subtitle')}
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '40px',
          }}
        >
          {reviewsData.map((review, index) => (
            <div
              key={review.id || index}
              className="animate-slide-up opacity-0"
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}
            >
              <ReviewCard
                review={review}
                verifiedPurchaseText={t('reviews.verifiedPurchase')}
              />
            </div>
          ))}
        </div>

        <div className="text-center">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              background: 'rgba(251,191,36,0.08)',
              border: '1px solid rgba(251,191,36,0.15)',
              padding: '16px 28px',
              borderRadius: '16px',
            }}
          >
            <span style={{ color: '#fbbf24', fontSize: '20px' }}>★★★★★</span>
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#fbbf24' }}>4.9/5</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                {t('reviews.appStoreRating')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
